// feature_slider — ported from builder/components/sections/FeatureSlider/FeatureSlider.mdx (## JS).
// Auto-advancing slider: the active tab reveals its body and fills a progress
// bar over `autoplay` ms, then advances (wrapping). Clicking a tab jumps to it
// and resets the timer; hovering the tab list pauses (banking the remaining
// time); an IntersectionObserver pauses while the section is off-screen. On
// tablet/mobile the media tile follows the open slide via the --before-media
// class (see feature_slider.sass). Reimplements the bar with a CSS width
// transition — no progressbar.js.
import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = ['tab', 'media', 'fill']
  static values = { autoplay: { type: Number, default: 15000 } }

  connect() {
    this.current = Math.max(
      this.tabTargets.findIndex(t =>
        t.classList.contains('feature-slider__tab--active')
      ),
      0
    )
    this.timer = null
    this.paused = false
    this.hovering = false
    this.remaining = this.autoplayValue
    this.startTime = 0

    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(
        entries =>
          entries.forEach(e =>
            e.isIntersecting ? this.resume() : this.pause()
          ),
        { threshold: 0.4 }
      )
      this.observer.observe(this.element)
    }

    this.#go(this.current)
  }

  disconnect() {
    clearTimeout(this.timer)
    this.observer?.disconnect()
  }

  activate(event) {
    const i = Number(event.currentTarget.dataset.index)
    if (Number.isNaN(i)) return
    this.#go(i)
  }

  // Hover pause/resume (tab list mouseenter/mouseleave). Tracked separately so
  // a tab CLICK while still hovering doesn't restart autoplay under the cursor.
  hoverPause() {
    this.hovering = true
    this.pause()
  }

  hoverResume() {
    this.hovering = false
    this.resume()
  }

  pause() {
    if (this.paused) return
    this.paused = true
    clearTimeout(this.timer)
    this.remaining -= Date.now() - this.startTime
    const f = this.fillTargets[this.current]
    if (f) {
      const w = getComputedStyle(f).width
      f.style.transition = 'none'
      f.style.width = w
    }
  }

  resume() {
    if (!this.paused) return
    this.paused = false
    const ms = Math.max(this.remaining, 0)
    const f = this.fillTargets[this.current]
    if (f) void f.offsetWidth
    this.#setFill(this.current, 100, ms)
    this.startTime = Date.now()
    this.timer = setTimeout(() => this.#next(), ms)
  }

  #go(i) {
    this.current = i
    this.tabTargets.forEach((t, k) => {
      const on = k === i
      t.classList.toggle('feature-slider__tab--active', on)
      // tablet/mobile: tabs before the active one sit ABOVE the media tile
      t.classList.toggle('feature-slider__tab--before-media', k < i)
      t.setAttribute('aria-selected', String(on))
      t.setAttribute('aria-expanded', String(on))
      if (!on) this.#setFill(k, 0, 0)
    })
    this.mediaTargets.forEach((m, k) => {
      m.classList.toggle('feature-slider__media--active', k === i)
      m.setAttribute('aria-hidden', String(k !== i))
    })
    this.#startProgress()
  }

  #startProgress() {
    clearTimeout(this.timer)
    this.remaining = this.autoplayValue
    this.#setFill(this.current, 0, 0)
    // Cursor still over the tab list (e.g. the user CLICKED a tab): stay
    // paused with the full duration banked — autoplay resumes on mouseleave.
    if (this.hovering) {
      this.paused = true
      return
    }
    this.paused = false
    const f = this.fillTargets[this.current]
    if (f) void f.offsetWidth // force reflow so 0% applies before animating
    this.#setFill(this.current, 100, this.autoplayValue)
    this.startTime = Date.now()
    this.timer = setTimeout(() => this.#next(), this.autoplayValue)
  }

  #next() {
    this.#go((this.current + 1) % this.tabTargets.length)
  }

  #setFill(i, pct, ms) {
    const f = this.fillTargets[i]
    if (!f) return
    f.style.transition = ms ? `width ${ms}ms linear` : 'none'
    f.style.width = `${pct}%`
  }
}
